import React, { useState, useEffect } from 'react'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import defaultTexts from '../../../texts/texts'
import { servicesSuspendPolicy, servicesFinalPolicy } from '../_common/billingConstants'
import { checkAccessRequest } from '../../../_network/request'
import CustomTextField from '../../_common/_form_fields/CustomTextField'
import CustomSelectField from '../../_common/_form_fields/CustomSelectField'
import CustomCheckboxField from '../../_common/_form_fields/CustomCheckboxField'
import CustomDialog from '../../_common/CustomDialog'

const OpenStackServicesEditRegion = (props) => {
  const {
    service_name,
    service_domain,
    service_version,
    is_identity_provider,
    region_name,
    credentials,
    resource_suspend_period,
    resource_delete_period,
    resource_suspend_policy,
    resource_delete_policy,
    setConfigs,
    api_version_list,
    identityProviderExists,
    billingServicesConfigurable,
    errors,
    validateFormFields,
  } = props
  const theme = useTheme()
  
  const [serviceDomain, setServiceDomain] = useState('')
  const [apiVersion, setApiVersion] = useState('')
  const [isIdentityProvider, setIsIdentityProvider] = useState(service_name === 'identity_keystone' ? true : false)
  const [regionName, setRegionName] = useState('')
  const [appCredentialId, setAppCredentialId] = useState('')
  const [appCredentialSecret, setAppCredentialSecret] = useState('')
  const [suspendAction, setSuspendAction] = useState('')
  const [suspendDelay, setSuspendDelay] = useState('')
  const [finalAction, setFinalAction] = useState('')
  const [finalActionDelay, setFinalActionDelay] = useState('')

  const [helperText, setHelperText] = useState('')
  const [helperDialogOpen, setHelperDialogOpen] = useState(false)
  const [domainAccess, setDomainAccess] = useState(defaultTexts.addSubscriptionResponsePlaceholder)

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.vodka,
    borderRadius: '5px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.vodka,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
  }

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const helpIconStyle = {
    color: theme.palette.oxfordBlue,
    marginTop: '-5px',
  }

  const responseTextStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
    borderRadius: '20px',
    padding: '20px',
  }

  const checkboxStyle = {
    color: theme.palette.violetsAreBlue,

    '&.Mui-checked': {
      color: theme.palette.violetsAreBlue,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const checkboxLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontWeight: '500',
  }

  const checkAccessButtonStyle = {
    fontWeight: '400',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '44px',
    borderRadius: '22px',
    padding: '0px 40px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const getError = (field_key) => {
    const field_key_name = service_name + '-' + region_name + '-' + field_key
    const error_object = errors.find(item => item.field_key === field_key_name)
    return error_object ? true : false
  }

  const updateConfigs = (field_key, value) => {
    setConfigs(prev => {
      let updated_data = []

      prev.forEach(item => {
        if (item.service_name === service_name && item.region_name === region_name) {
          updated_data.push({ ...item, [field_key]: value })
        } else {
          updated_data.push(item)
        }
      })

      return updated_data
    })

    validateFormFields()
  }

  const handleHelperDialogOpen = (value) => {
    setHelperDialogOpen(true)
    setHelperText(value)
  }

  const handleHelperDialogClose = () => {
    setHelperDialogOpen(false)
    setHelperText('')
  }

  const handleChangeServiceDomain = (e) => {
    const value = e.target.value
    setServiceDomain(value)
    updateConfigs('service_domain', value)
    setDomainAccess(defaultTexts.addSubscriptionResponsePlaceholder)
  }

  const handleChangeApiVersion = (value) => {
    setApiVersion(value)
    updateConfigs('service_version', value)
  }

  const handleChangeIsIdentityProvider = (value) => {
    setIsIdentityProvider(value)
    updateConfigs('is_identity_provider', value)
  }

  const handleChangeRegionName = (e) => {
    const value = e.target.value
    setRegionName(value)
    updateConfigs('region_name', value)
  }

  const handleChangeAppCredentialId = (e) => {
    const value = e.target.value
    setAppCredentialId(value)
    const credentials_new = { ...credentials, app_auth_id: value }
    updateConfigs('credentials', credentials_new)
  }

  const handleChangeAppCredentialSecret = (e) => {
    const value = e.target.value
    setAppCredentialSecret(value)
    const credentials_new = { ...credentials, app_auth_secret: value }
    updateConfigs('credentials', credentials_new)
  }

  const handleChangeSuspendAction = (value) => {
    setSuspendAction(value)
    updateConfigs('resource_suspend_policy', value)
  }

  const handleChangeSuspendDelay = (e) => {
    const value = e.target.value
    setSuspendDelay(value)
    updateConfigs('resource_suspend_period', parseInt(value))
  }

  const handleChangeFinalAction = (value) => {
    setFinalAction(value)
    updateConfigs('resource_delete_policy', value)
  }

  const handleChangeFinalActionDelay = (e) => {
    const value = e.target.value
    setFinalActionDelay(value)
    updateConfigs('resource_delete_period', parseInt(value))
  }

  const handleCheckAccess = async () => {
    setDomainAccess('loading...')

    const response = await checkAccessRequest(service_domain)    
    
    if (response && response.data) {
      setDomainAccess(response.data)
    } else {
      setDomainAccess(defaultTexts.anErrorOccured)
    }
  }
  
  useEffect(() => {
    setServiceDomain(service_domain ? service_domain : '')
  }, [service_domain])

  useEffect(() => {
    setApiVersion(service_version ? service_version : '')
  }, [service_version])

  useEffect(() => {
    setIsIdentityProvider(is_identity_provider ? is_identity_provider : false)
  }, [is_identity_provider])

  useEffect(() => {
    setRegionName(region_name ? region_name : '')
  }, [region_name])

  useEffect(() => {
    setSuspendAction(resource_suspend_policy ? resource_suspend_policy : '')
  }, [resource_suspend_policy])

  useEffect(() => {
    setSuspendDelay(resource_suspend_period ? resource_suspend_period : '')
  }, [resource_suspend_period])

  useEffect(() => {
    setFinalAction(resource_delete_policy ? resource_delete_policy : '')
  }, [resource_delete_policy])

  useEffect(() => {
    setFinalActionDelay(resource_delete_period ? resource_delete_period : '')
  }, [resource_delete_period])

  return (
    <>
      <Box sx={{ padding: '20px' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 6 }}>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomTextField 
                    variant='outlined'
                    label={defaultTexts.serviceDomain}
                    currentValue={serviceDomain}
                    setCurrentValue={handleChangeServiceDomain}
                    required={true}
                    error={getError('service_domain')}
                    disabled={!billingServicesConfigurable}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.serviceDomainHelperText)}>
                    <HelpOutlineIcon fontSize='large' />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomSelectField
                    label={defaultTexts.apiVersion}
                    items={api_version_list ? api_version_list : []}
                    currentValue={apiVersion}
                    setCurrentValue={handleChangeApiVersion}
                    required={true}
                    error={getError('api_version')}
                    disabled={!billingServicesConfigurable}
                    self_item_titles={true}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomCheckboxField
                    label={defaultTexts.isIdentityProvider}
                    checked={isIdentityProvider}
                    handleChange={handleChangeIsIdentityProvider}
                    disabled={(identityProviderExists && !isIdentityProvider) || !billingServicesConfigurable}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.isIdentityProviderHelperText)}>
                    <HelpOutlineIcon fontSize='large' />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomTextField 
                    variant='outlined'
                    label={defaultTexts.regionName}
                    currentValue={regionName}
                    setCurrentValue={handleChangeRegionName}
                    required={true}
                    error={getError('region_name')}
                    disabled={!billingServicesConfigurable}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
              </Grid>
            </Box>

            {
              isIdentityProvider &&
              <>
                <Box sx={fieldBoxStyle}>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 10 }}>
                      <CustomTextField 
                        variant='outlined'
                        label={defaultTexts.appCredentialId}
                        currentValue={appCredentialId}
                        setCurrentValue={handleChangeAppCredentialId}
                        required={true}
                        error={getError('app_credential_id')}
                        disabled={!billingServicesConfigurable}
                        size='small'
                        sx={formControlStyle}
                        outlinedInputSx={outlinedInputStyle}
                      />
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.appCredentialIdHelperText)}>
                        <HelpOutlineIcon fontSize='large' />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={fieldBoxStyle}>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 10 }}>
                      <CustomTextField 
                        variant='outlined'
                        label={defaultTexts.appCredentialSecret}
                        currentValue={appCredentialSecret}
                        setCurrentValue={handleChangeAppCredentialSecret}
                        required={true}
                        error={getError('app_credential_secret')}
                        disabled={!billingServicesConfigurable}
                        size='small'
                        sx={formControlStyle}
                        outlinedInputSx={outlinedInputStyle}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </>
            }

          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            
            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomSelectField
                    label={defaultTexts.suspendAction}
                    items={service_name ? servicesSuspendPolicy[service_name] : []} 
                    currentValue={suspendAction}
                    setCurrentValue={handleChangeSuspendAction}
                    required={true}
                    error={getError('resource_suspend_policy')}
                    item_titles={defaultTexts}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.suspendActionHelperText)}>
                    <HelpOutlineIcon fontSize='large' />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomTextField
                    type='number'
                    min={1}
                    variant='outlined'
                    label={defaultTexts.suspendDelayMinutes}
                    currentValue={suspendDelay}
                    setCurrentValue={handleChangeSuspendDelay}
                    required={true}
                    error={getError('resource_suspend_period')}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomSelectField
                    label={defaultTexts.finalAction}
                    items={service_name ? servicesFinalPolicy[service_name] : []} 
                    currentValue={finalAction}
                    setCurrentValue={handleChangeFinalAction}
                    required={true}
                    error={getError('resource_delete_policy')}
                    item_titles={defaultTexts}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 2 }}>
                  <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.finalActionHelperText)}>
                    <HelpOutlineIcon fontSize='large' />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>

            <Box sx={fieldBoxStyle}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 10 }}>
                  <CustomTextField
                    type='number'
                    min={1}
                    variant='outlined'
                    label={defaultTexts.finalActionDelayMinutes}
                    currentValue={finalActionDelay}
                    setCurrentValue={handleChangeFinalActionDelay}
                    required={true}
                    error={getError('resource_delete_period')}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Grid>
              </Grid>
            </Box>

          </Grid>
        </Grid>

        <Stack
          direction='row'
          justifyContent='flex-end'
          sx={{ margin: '20px 0px' }}
        >
          <Button
            variant='text'
            disableRipple
            disableElevation
            disabled={!serviceDomain ? true : false}
            onClick={handleCheckAccess}
            sx={checkAccessButtonStyle}
          >
            {defaultTexts.checkAccess}
          </Button>
        </Stack>
        <Box sx={responseTextStyle}>
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            { domainAccess ? JSON.stringify(domainAccess, null, 2) : defaultTexts.anErrorOccured }
          </pre>
        </Box>

      </Box>

      <CustomDialog
        open={helperDialogOpen}
        onClose={handleHelperDialogClose}
        dialogBody={{
          text: helperText,
        }}
      />
    </>
  )
}

export default OpenStackServicesEditRegion